import React, { useState, useEffect } from "react";
import { Circle, Plus, ArchiveIcon, PackagePlusIcon, ShieldCheckIcon, Link } from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useNotification } from "../../../hooks";
import { useGetCollections, useGetTestCasesByEndpoint, useCreateApiChain } from "../../../useQuery";

const ChainCreationModal = ({ open, onClose, onChainCreated }) => {
  const notify = useNotification();
  const [collections, setCollections] = useState([]);
  const [expandedCollections, setExpandedCollections] = useState({});
  const [expandedEndpoints, setExpandedEndpoints] = useState({});
  const [testCasesByEndpoint, setTestCasesByEndpoint] = useState({});
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  
  const [chainName, setChainName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [description, setDescription] = useState("");
  const [criteria, setCriteria] = useState("");
  const [testType, setTestType] = useState("integration");

  const { mutate: fetchCollections, isPending: loadingCollections } = useGetCollections({
    onSuccess: (data) => {
      setCollections(data || []);
    },
    onError: (error) => {
      notify("Error fetching collections", "error");
    },
  });

  const { mutate: fetchTestCases, isPending: loadingTestCases } = useGetTestCasesByEndpoint({
    onSuccess: (data, variables) => {
      setTestCasesByEndpoint((prev) => ({
        ...prev,
        [variables.endpointId]: data || [],
      }));
    },
    onError: (error) => {
      notify("Error fetching test cases", "error");
    },
  });

  const { mutate: createChain, isPending: creatingChain } = useCreateApiChain({
    onSuccess: (data) => {
      notify("Chain created successfully!", "success");
      onChainCreated && onChainCreated(data);
      handleClose();
    },
    onError: (error) => {
      notify(`Error creating chain: ${error}`, "error");
    },
  });

  useEffect(() => {
    if (open) {
      fetchCollections();
      setExpandedCollections({});
      setExpandedEndpoints({});
      setTestCasesByEndpoint({});
      setSelectedTestCases([]);
      setChainName("");
      setBaseUrl("");
      setDescription("");
      setCriteria("");
      setTestType("integration");
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleCollectionChange = (collectionId) => (event, isExpanded) => {
    setExpandedCollections((prev) => ({
      ...prev,
      [collectionId]: isExpanded,
    }));

    if (isExpanded) {
      const collection = collections.find((c) => c.id === collectionId);
      if (collection && collection.endpoints) {
        collection.endpoints.forEach((endpoint) => {
          if (!testCasesByEndpoint[endpoint.id]) {
            fetchTestCases({ endpointId: endpoint.id });
          }
        });
      }
    }
  };

  const handleEndpointChange = (endpointId) => (event, isExpanded) => {
    setExpandedEndpoints((prev) => ({
      ...prev,
      [endpointId]: isExpanded,
    }));
  };

  const handleTestCaseSelect = (testCase, endpointId, collectionId) => {
    const testCaseData = {
      id: testCase.id,
      testCase: testCase.testCase,
      endpointId,
      collectionId,
    };

    setSelectedTestCases((prev) => {
      const exists = prev.find((tc) => tc.id === testCaseData.id);
      if (exists) {
        return prev.filter((tc) => tc.id !== testCaseData.id);
      } else {
        return [...prev, testCaseData];
      }
    });
  };

  const handleCreateChain = () => {
    if (!chainName.trim()) {
      notify("Please enter a chain name", "error");
      return;
    }
    if (!baseUrl.trim()) {
      notify("Please enter a base URL", "error");
      return;
    }
    if (selectedTestCases.length === 0) {
      notify("Please select at least one test case", "error");
      return;
    }

    const chainData = {
      name: chainName,
      baseUrl: baseUrl,
      description: description,
      criteria: criteria,
      testType: testType,
      testCases: selectedTestCases.map((tc) => tc.id),
    };

    createChain({ data: chainData });
  };

  const getMethodStyles = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":
        return {
          backgroundColor: "#f0f9ff",
          color: "#065f46",
          borderColor: "#10b981",
        };
      case "POST":
        return {
          backgroundColor: "#eff6ff",
          color: "#1e40af",
          borderColor: "#3b82f6",
        };
      case "PUT":
        return {
          backgroundColor: "#fff7ed",
          color: "#c2410c",
          borderColor: "#f97316",
        };
      case "DELETE":
        return {
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          borderColor: "#ef4444",
        };
      case "PATCH":
        return {
          backgroundColor: "#faf5ff",
          color: "#7c3aed",
          borderColor: "#8b5cf6",
        };
      default:
        return {
          backgroundColor: "#f9fafb",
          color: "#374151",
          borderColor: "#6b7280",
        };
    }
  };

  const isTestCaseSelected = (testCaseId) => {
    return selectedTestCases.some((tc) => tc.id === testCaseId);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <p className="font-apple text-[20px] font-medium">Create New Chain</p>
        <Link className="ml-4" size={24} />
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          backgroundColor: "#ffffff",
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Form Section */}
        <Box
          sx={{
            padding: "20px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
            flexShrink: 0,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Chain Name"
              value={chainName}
              onChange={(e) => setChainName(e.target.value)}
              fullWidth
              required
              size="small"
              placeholder="Enter chain name"
            />
            <TextField
              label="Base URL"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              fullWidth
              required
              size="small"
              placeholder="https://api.example.com"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Describe what this chain tests"
            />
            <TextField
              label="Criteria"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Define success criteria for this chain"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Test Type</InputLabel>
              <Select
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                label="Test Type"
              >
                <MenuItem value="integration">Integration</MenuItem>
                <MenuItem value="functional">Functional</MenuItem>
                <MenuItem value="regression">Regression</MenuItem>
                <MenuItem value="smoke">Smoke</MenuItem>
              </Select>
            </FormControl>
          </div>
        </Box>

        <Box
          className="font-apple"
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#a1a1a1",
            },
          }}
        >
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Test Cases</h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose test cases from your collections to include in this chain
            </p>
            {selectedTestCases.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  Selected Test Cases ({selectedTestCases.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedTestCases.map((tc) => (
                    <Chip
                      key={tc.id}
                      label={tc.testCase}
                      size="small"
                      onDelete={() => handleTestCaseSelect({ id: tc.id }, tc.endpointId, tc.collectionId)}
                      sx={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {loadingCollections ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Typography variant="body2" color="text.secondary">
                Loading collections...
              </Typography>
            </Box>
          ) : collections.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={12}>
              <ArchiveIcon sx={{ fontSize: 18, color: "text.disabled", mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                No collections found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: "100%" }}>
              {collections.map((collection) => (
                <Accordion
                  key={collection.id}
                  expanded={expandedCollections[collection.id] || false}
                  onChange={handleCollectionChange(collection.id)}
                  sx={{
                    mb: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:before": {
                      display: "none",
                    },
                    "&.Mui-expanded": {
                      margin: "0 0 16px 0",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "#fafafa",
                      borderBottom: "1px solid #e0e0e0",
                      "&:hover": {
                        backgroundColor: "rgba(25,118,210,0.08)",
                      },
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                      },
                    }}
                  >
                    <ArchiveIcon size={18} />
                    <p className="font-apple ml-4 text-sm font-medium">{collection.name}</p>
                    <Chip
                      label={`${collection.endpoints?.length || 0} endpoints`}
                      size="small"
                      sx={{ ml: 2 }}
                      variant="outlined"
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {collection.endpoints?.map((endpoint) => {
                      const methodStyles = getMethodStyles(endpoint.method);

                      return (
                        <Accordion
                          key={endpoint.id}
                          expanded={expandedEndpoints[endpoint.id] || false}
                          onChange={handleEndpointChange(endpoint.id)}
                          sx={{
                            "&:before": {
                              display: "none",
                            },
                            "&.Mui-expanded": {
                              margin: 0,
                            },
                            boxShadow: "none",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              backgroundColor: "#f9f9f9",
                              minHeight: "48px",
                              "&.Mui-expanded": {
                                minHeight: "48px",
                              },
                              "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                margin: "8px 0",
                              },
                            }}
                          >
                            <Circle size={4} className="text-gray-400 fill-current mr-3" />
                            <Chip
                              label={endpoint.method}
                              size="small"
                              sx={{
                                ...methodStyles,
                                fontSize: "10px",
                                height: "20px",
                                minWidth: "60px",
                                mr: 2,
                              }}
                            />
                            <p className="font-apple text-sm font-medium">{endpoint.path}</p>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pl: 6, pr: 2, py: 2 }}>
                            {loadingTestCases ? (
                              <Typography variant="caption" color="text.secondary">
                                Loading test cases...
                              </Typography>
                            ) : testCasesByEndpoint[endpoint.id]?.length > 0 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {testCasesByEndpoint[endpoint.id].map((testCase) => {
                                  const isSelected = isTestCaseSelected(testCase.id);
                                  return (
                                    <Box
                                      key={testCase.id}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        p: 2,
                                        backgroundColor: isSelected ? "#f0f9ff" : "white",
                                        border: isSelected ? "2px solid #3b82f6" : "1px solid #e0e0e0",
                                        borderRadius: "4px",
                                        "&:hover": {
                                          backgroundColor: isSelected ? "#e0f2fe" : "#f9f9f9",
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <ShieldCheckIcon 
                                          size={14} 
                                          className={isSelected ? "text-blue-500" : "text-green-500"} 
                                        />
                                        <Box>
                                          <p className="font-apple text-sm">{testCase.testCase}</p>
                                          <p className="text-[12px] text-blue-500">
                                            {testCase.testType}
                                          </p>
                                        </Box>
                                      </Box>
                                      <button
                                        onClick={() =>
                                          handleTestCaseSelect(testCase, endpoint.id, collection.id)
                                        }
                                        disabled={creatingChain}
                                        className={`flex items-center px-3 py-1 transition-all duration-200 text-xs cursor-pointer disabled:opacity-50 rounded ${
                                          isSelected
                                            ? "bg-blue-600 text-white hover:bg-blue-700"
                                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        }`}
                                      >
                                        <Plus size={12} className="mr-1" />
                                        {isSelected ? "Selected" : "Select"}
                                      </button>
                                    </Box>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                                No test cases found
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          gap: "12px",
          flexShrink: 0,
        }}
      >
        <button
          onClick={handleClose}
          className="px-4 py-2 font-apple text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 cursor-pointer border border-gray-300 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleCreateChain}
          disabled={creatingChain || !chainName.trim() || !baseUrl.trim() || selectedTestCases.length === 0}
          className="px-4 py-2 font-apple text-sm text-white transition-all duration-200 cursor-pointer bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
        >
          {creatingChain ? "Creating..." : "Create Chain"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ChainCreationModal;
